import type { PartialBlock } from '@blocknote/core';

export const DEFERRED_CUSTOM_BLOCK_TYPES = ['dropdown', 'date'] as const;

// horizontalRule React node views can crash while BlockNote builds the initial
// document ("Cannot find node position" from the selection plugin calling
// getPos() before ProseMirror registers the node view). Editable mounts defer
// them like dropdown/date blocks and restore them once node positions are
// stable. Read-only mounts never mount horizontalRule node views at all (see
// stripReadOnlyProblemBlocks), so they are not deferred there.
export const EDITABLE_DEFERRED_CUSTOM_BLOCK_TYPES = [
  ...DEFERRED_CUSTOM_BLOCK_TYPES,
  'horizontalRule',
] as const;

type DeferredCustomBlockType =
  (typeof EDITABLE_DEFERRED_CUSTOM_BLOCK_TYPES)[number];

export function isDeferredCustomBlockType(
  blockType?: string,
  editable = false,
): blockType is DeferredCustomBlockType {
  if (!blockType) {
    return false;
  }

  const deferredTypes: readonly string[] = editable
    ? EDITABLE_DEFERRED_CUSTOM_BLOCK_TYPES
    : DEFERRED_CUSTOM_BLOCK_TYPES;

  return deferredTypes.includes(blockType);
}

export function filterDeferredCustomBlocks(
  blocks: PartialBlock[],
  editable = false,
): PartialBlock[] {
  return blocks.filter((block) => {
    const blockType = (block as { type?: string }).type;
    return !isDeferredCustomBlockType(blockType, editable);
  });
}

export function findDeferredBlockInsertAfterId(
  blocks: PartialBlock[],
  index: number,
  editable = false,
): string | null {
  for (let currentIndex = index - 1; currentIndex >= 0; currentIndex -= 1) {
    const previousBlock = blocks[currentIndex];
    const previousBlockType = (previousBlock as { type?: string }).type;
    if (
      !isDeferredCustomBlockType(previousBlockType, editable) &&
      previousBlock?.id
    ) {
      return previousBlock.id;
    }
  }

  return null;
}
